import React, { useCallback, useMemo } from "react";
import { Alert, Chip, Stack, Typography } from "@mui/material";
import SelectableCopyList from "../components/SelectableCopyList";
import { useSourceAgents } from "../hooks/useSourceEntities";
import { useAlreadyCopied, useCopyAgents } from "../hooks/useCopyLog";
import type { CopyOperationResult } from "../types";

interface Props {
  destPortalId: string;
  sourcePortalId: string;
  onResult: (r: CopyOperationResult) => void;
}

const AgentsImportPanel: React.FC<Props> = ({
  destPortalId,
  sourcePortalId,
  onResult,
}) => {
  const { data: agents = [], isLoading: loadingList } =
    useSourceAgents(sourcePortalId || null);
  const { data: copied, isLoading: loadingCopied } = useAlreadyCopied(
    destPortalId,
    sourcePortalId || null,
    "agent",
  );
  const copyMutation = useCopyAgents();

  // Build a set of all agent IDs for quick lookup
  const agentIdSet = useMemo(() => new Set(agents.map((a) => a.id)), [agents]);

  // Returns IDs that should be auto-selected when a workflow agent is checked
  const getAutoSelectIds = useCallback(
    (selectedIds: string[]) => {
      const deps: string[] = [];
      for (const id of selectedIds) {
        const agent = agents.find((a) => a.id === id);
        if (agent?.agent_type === "graph" && agent.depends_on_agent_ids) {
          for (const depId of agent.depends_on_agent_ids) {
            if (agentIdSet.has(depId)) deps.push(depId);
          }
        }
      }
      return deps;
    },
    [agents, agentIdSet],
  );

  if (!sourcePortalId) {
    return <Alert severity="info">Select a source portal above to see its agents.</Alert>;
  }

  const rows = agents.map((a) => {
    const isWorkflow = a.agent_type === "graph";
    const depCount = a.depends_on_agent_ids?.filter((id) => agentIdSet.has(id)).length ?? 0;
    return {
      id: a.id,
      primary: a.name,
      secondary: isWorkflow && depCount > 0
        ? `Workflow with ${depCount} agent${depCount === 1 ? "" : "s"} — will be auto-included`
        : a.description ?? undefined,
      meta: (
        <Stack direction="row" spacing={0.5}>
          {isWorkflow && (
            <Chip label="Workflow" size="small" color="secondary" variant="outlined" />
          )}
          <Chip label={a.provider} size="small" variant="outlined" />
          <Chip label={a.model} size="small" variant="outlined" />
        </Stack>
      ),
    };
  });

  const handleCopy = async (ids: string[]) => {
    const items = ids.map((id) => {
      const agent = agents.find((a) => a.id === id)!;
      return { id: agent.id, name: agent.name };
    });
    const result = await copyMutation.mutateAsync({
      destPortalId,
      sourcePortalId,
      items,
    });
    onResult(result);
  };

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        Agents are copied with system prompts, model config, and retrieval
        settings — but <strong>knowledge base links will be cleared</strong>.
        After creating KBs in this portal, re-link them on each agent.
      </Alert>
      <Alert severity="info">
        Selecting a <strong>workflow agent</strong> will automatically include
        the agents it depends on.
      </Alert>
      <Typography variant="caption" color="text.secondary">
        Showing {agents.length} agent{agents.length === 1 ? "" : "s"} from the source portal.
      </Typography>
      <SelectableCopyList
        rows={rows}
        alreadyCopiedIds={copied?.source_entity_ids ?? []}
        loading={loadingList || loadingCopied}
        emptyMessage="No agents in the source portal."
        onCopy={handleCopy}
        isCopying={copyMutation.isPending}
        ctaLabel="Copy selected agents"
        getAutoSelectIds={getAutoSelectIds}
      />
    </Stack>
  );
};

export default AgentsImportPanel;
